import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ChevronLeft, ChevronRight, ListChecks } from 'lucide-react';
import { differenceInDays, parseISO, format } from 'date-fns';
import { fmtPatron } from '../utils/dates';
import { es } from 'date-fns/locale';

function urgencia(dias) {
  if (dias <= 7) return 'var(--red, #e06666)';
  if (dias <= 30) return 'var(--amber)';
  return 'var(--accent)';
}

export default function VencimientosWidget({ policies, clients }) {
  const navigate = useNavigate();
  const today = new Date();
  const [mesOffset, setMesOffset] = useState(0);
  const [filtro, setFiltro] = useState('todas'); // 'todas' | 'criticas'

  const getClient = (id) => clients.find(c => c.id === id) || {};

  const activas = useMemo(() =>
    policies
      .filter(p => (p.status === 'active' || p.status === 'endorsed') && p.endDate)
      .map(p => ({ ...p, dias: differenceInDays(parseISO(p.endDate), today) })),
  [policies]);

  // Resumen
  const resumen = useMemo(() => ({
    semana: activas.filter(p => p.dias >= 0 && p.dias <= 7).length,
    mes: activas.filter(p => p.dias >= 0 && p.dias <= 30).length,
    noventa: activas.filter(p => p.dias >= 0 && p.dias <= 90).length,
    criticas: activas.filter(p => p.critical).length,
  }), [activas]);

  // Lista de próximos vencimientos (ordenada por urgencia)
  const lista = useMemo(() => {
    let l = activas.filter(p => p.dias >= -30); // incluye vencidas recientes
    if (filtro === 'criticas') l = l.filter(p => p.critical);
    return l.sort((a, b) => a.dias - b.dias).slice(0, 40);
  }, [activas, filtro]);

  // Calendario
  const mesView = useMemo(() => new Date(today.getFullYear(), today.getMonth() + mesOffset, 1), [mesOffset]);
  const vencDelMes = useMemo(() => {
    const map = {};
    activas.forEach(p => {
      const fv = parseISO(p.endDate);
      if (fv.getFullYear() === mesView.getFullYear() && fv.getMonth() === mesView.getMonth()) {
        const dia = fv.getDate();
        if (!map[dia]) map[dia] = [];
        map[dia].push(p);
      }
    });
    return map;
  }, [activas, mesView]);

  const diasEnMes = new Date(mesView.getFullYear(), mesView.getMonth() + 1, 0).getDate();
  let primerDia = new Date(mesView.getFullYear(), mesView.getMonth(), 1).getDay();
  primerDia = (primerDia + 6) % 7;
  const esHoy = (dia) =>
    mesView.getFullYear() === today.getFullYear() &&
    mesView.getMonth() === today.getMonth() && dia === today.getDate();

  const StatCard = ({ label, value, color }) => (
    <div style={{ background: 'var(--bg-input)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)', color: color || 'var(--text-primary)' }}>{value}</div>
    </div>
  );

  return (
    <div>
      {/* Resumen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        <StatCard label="Vence esta semana" value={resumen.semana} color="var(--red, #e06666)" />
        <StatCard label="Este mes" value={resumen.mes} color="var(--amber)" />
        <StatCard label="Próximos 90 días" value={resumen.noventa} />
        <StatCard label="Críticas" value={resumen.criticas} color="var(--amber)" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 16, alignItems: 'start' }}>

        {/* Lista de vencimientos */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
              <ListChecks size={15} /> Próximos vencimientos
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 12, background: filtro === 'todas' ? 'var(--accent-dim)' : undefined, color: filtro === 'todas' ? 'var(--accent)' : undefined }} onClick={() => setFiltro('todas')}>Todas</button>
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 12, background: filtro === 'criticas' ? 'var(--amber-dim)' : undefined, color: filtro === 'criticas' ? 'var(--amber)' : undefined }} onClick={() => setFiltro('criticas')}>Solo críticas</button>
            </div>
          </div>

          {lista.length === 0 ? (
            <div style={{ background: 'var(--bg-card)', border: '1px dashed var(--border)', borderRadius: 10, padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              {filtro === 'criticas' ? 'No hay cuentas críticas con vencimiento próximo.' : 'No hay vencimientos próximos.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {lista.map(p => {
                const c = getClient(p.clientId);
                const color = urgencia(p.dias);
                return (
                  <div key={p.id} onClick={() => navigate(`/policies/${p.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderLeft: `3px solid ${color}`, borderRadius: 10, cursor: 'pointer', transition: 'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-input)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}>
                    <div style={{ textAlign: 'center', minWidth: 42 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)', color }}>
                        {p.dias < 0 ? '!' : p.dias}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{p.dias < 0 ? 'vencida' : 'días'}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.name || 'Sin cliente'}
                        {p.critical && <Star size={12} color="var(--amber)" fill="var(--amber)" style={{ marginLeft: 6, verticalAlign: 'middle' }} />}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p.number} · {p.company} · {fmtPatron(p.endDate, 'dd/MM/yyyy')}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Calendario compacto */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
              {format(mesView, 'MMMM yyyy', { locale: es })}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setMesOffset(m => m - 1)} style={{ padding: '3px 7px' }}><ChevronLeft size={13} /></button>
              {mesOffset !== 0 && <button className="btn btn-secondary btn-sm" onClick={() => setMesOffset(0)} style={{ fontSize: 11 }}>Hoy</button>}
              <button className="btn btn-secondary btn-sm" onClick={() => setMesOffset(m => m + 1)} style={{ padding: '3px 7px' }}><ChevronRight size={13} /></button>
            </div>
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, textAlign: 'center', fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>
              {['L','M','M','J','V','S','D'].map((d, i) => <div key={i}>{d}</div>)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
              {Array.from({ length: primerDia }).map((_, i) => <div key={'e'+i} />)}
              {Array.from({ length: diasEnMes }).map((_, i) => {
                const dia = i + 1;
                const vencs = vencDelMes[dia] || [];
                const hayCritica = vencs.some(p => p.critical);
                let bg = 'transparent', fg = 'var(--text-secondary)', fw = 400;
                if (vencs.length > 0) {
                  fw = 700;
                  if (hayCritica) { bg = 'var(--red-dim, rgba(220,80,80,0.18))'; fg = 'var(--red, #e06666)'; }
                  else { bg = 'rgba(80,140,220,0.18)'; fg = 'var(--blue)'; }
                }
                return (
                  <div key={dia}
                    title={vencs.length ? vencs.map(p => `${getClient(p.clientId).name} (${p.number})`).join('\n') : ''}
                    style={{ aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, fontSize: 11, fontWeight: fw, background: bg, color: fg, outline: esHoy(dia) ? '1.5px solid var(--accent)' : 'none' }}>
                    {dia}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 10, color: 'var(--text-secondary)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--red, #e06666)', display: 'inline-block' }} />crítica</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--blue)', display: 'inline-block' }} />normal</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
